// src/components/ui/TypeBadge.jsx
import React from "react";

/**
 * Canonical product-category chip styles.
 * Keys are normalized (lowercase, spaces/hyphens collapsed).
 */
const CATEGORY_STYLES = {
  btl: "bg-sky-100 text-sky-800 border-sky-200",
  "buy to let": "bg-sky-100 text-sky-800 border-sky-200",
  "buy-to-let": "bg-sky-100 text-sky-800 border-sky-200",
  "complex btl": "bg-violet-100 text-violet-800 border-violet-200",
  "complex-btl": "bg-violet-100 text-violet-800 border-violet-200",
  cbtl: "bg-violet-100 text-violet-800 border-violet-200",
  if: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "invoice finance": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "invoice-finance": "bg-emerald-100 text-emerald-800 border-emerald-200",
  sc: "bg-amber-100 text-amber-900 border-amber-200",
  "semi commercial": "bg-amber-100 text-amber-900 border-amber-200",
  "semi-commercial": "bg-amber-100 text-amber-900 border-amber-200",
  af: "bg-rose-100 text-rose-800 border-rose-200",
  "asset finance": "bg-rose-100 text-rose-800 border-rose-200",
  "asset-finance": "bg-rose-100 text-rose-800 border-rose-200",
  bridging: "bg-orange-100 text-orange-900 border-orange-200",
  development: "bg-teal-100 text-teal-800 border-teal-200",
  commercial: "bg-cyan-100 text-cyan-900 border-cyan-200",
};

const CATEGORY_LABELS = {
  btl: "BTL",
  "buy to let": "BTL",
  "buy-to-let": "BTL",
  "complex btl": "Complex BTL",
  "complex-btl": "Complex BTL",
  cbtl: "Complex BTL",
  if: "IF",
  "invoice finance": "IF",
  "invoice-finance": "IF",
  sc: "SC",
  "semi commercial": "SC",
  "semi-commercial": "SC",
  af: "AF",
  "asset finance": "AF",
  "asset-finance": "AF",
};

function normalizeCategoryKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_/]+/g, " ")
    .replace(/\s+/g, " ");
}

export function resolveCategoryBadge(value) {
  const raw = String(value || "").trim();
  if (!raw || raw === "—" || raw === "-") {
    return {
      label: "—",
      className: "bg-slate-100 text-slate-600 border-slate-200",
    };
  }
  const key = normalizeCategoryKey(raw);
  const hyphenKey = key.replace(/\s+/g, "-");
  const className =
    CATEGORY_STYLES[key] ||
    CATEGORY_STYLES[hyphenKey] ||
    "bg-slate-100 text-slate-700 border-slate-200";
  const label = CATEGORY_LABELS[key] || CATEGORY_LABELS[hyphenKey] || raw;
  return { label, className };
}

/** Product category badge (BTL, IF, SC, AF, Complex BTL, …) */
export default function TypeBadge({ t, className = "" }) {
  const { label, className: tone } = resolveCategoryBadge(t);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${tone} ${className}`}
    >
      {label}
    </span>
  );
}

const IF_TYPE_STYLES = {
  factoring: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "invoice discounting": "bg-sky-100 text-sky-800 border-sky-200",
  confidential: "bg-violet-100 text-violet-800 border-violet-200",
  selective: "bg-amber-100 text-amber-900 border-amber-200",
  "whole ledger": "bg-indigo-100 text-indigo-800 border-indigo-200",
};

const BTL_RATE_TYPE_STYLES = {
  fixed: "bg-sky-100 text-sky-800 border-sky-200",
  variable: "bg-violet-100 text-violet-800 border-violet-200",
  tracker: "bg-amber-100 text-amber-900 border-amber-200",
  discounted: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

/** BTL mortgage product type (Fixed, Variable, Tracker, Discounted) */
export function MortgageProductTypeBadge({ type }) {
  const label = String(type || "—").trim() || "—";
  const key = label.toLowerCase();
  const cls =
    BTL_RATE_TYPE_STYLES[key] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}
    >
      {label}
    </span>
  );
}

/** Invoice finance facility type (Factoring, Confidential, etc.) */
export function InvoiceFinanceTypeBadge({ type }) {
  const label = String(type || "—").trim() || "—";
  const key = label.toLowerCase();
  const cls =
    IF_TYPE_STYLES[key] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center justify-center px-2.5 py-1 rounded-full text-xs font-semibold border ${cls}`}
    >
      {label}
    </span>
  );
}

const SECTOR_STYLES = {
  haulage: "bg-amber-100 text-amber-900 border-amber-300",
  engineering: "bg-sky-100 text-sky-800 border-sky-300",
  construction: "bg-orange-100 text-orange-900 border-orange-300",
  manufacturing: "bg-emerald-100 text-emerald-800 border-emerald-300",
  wholesale: "bg-violet-100 text-violet-800 border-violet-300",
  retail: "bg-pink-100 text-pink-800 border-pink-300",
  transport: "bg-cyan-100 text-cyan-900 border-cyan-300",
  "professional services": "bg-indigo-100 text-indigo-800 border-indigo-300",
  healthcare: "bg-teal-100 text-teal-800 border-teal-300",
  recruitment: "bg-lime-100 text-lime-900 border-lime-300",
  logistics: "bg-yellow-100 text-yellow-900 border-yellow-300",
};

const SECTOR_PALETTE = [
  "bg-sky-100 text-sky-800 border-sky-300",
  "bg-emerald-100 text-emerald-800 border-emerald-300",
  "bg-violet-100 text-violet-800 border-violet-300",
  "bg-amber-100 text-amber-900 border-amber-300",
  "bg-rose-100 text-rose-800 border-rose-300",
  "bg-cyan-100 text-cyan-900 border-cyan-300",
  "bg-indigo-100 text-indigo-800 border-indigo-300",
  "bg-teal-100 text-teal-800 border-teal-300",
  "bg-orange-100 text-orange-900 border-orange-300",
  "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  "bg-lime-100 text-lime-900 border-lime-300",
  "bg-pink-100 text-pink-800 border-pink-300",
];

function sectorColorIndex(label) {
  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  }
  return hash % SECTOR_PALETTE.length;
}

/** Compact table label: keep the lead phrase before ", " / " and " / " & ". */
function shortSectorLabel(label) {
  const raw = String(label || "").trim();
  if (!raw) return "—";
  const lead = raw.split(/\s*,\s*|\s+&\s+|\s+and\s+/i)[0]?.trim();
  return lead || raw;
}

/** Invoice finance sector badge — consistent color per sector name */
export function SectorBadge({ sector, className = "", compact = false }) {
  const full = String(sector || "—").trim() || "—";
  const label = compact ? shortSectorLabel(full) : full;
  const key = full.toLowerCase();
  const cls =
    SECTOR_STYLES[key] ||
    SECTOR_STYLES[label.toLowerCase()] ||
    SECTOR_PALETTE[sectorColorIndex(key)];
  return (
    <span
      className={`inline-flex min-w-0 max-w-full items-center truncate rounded-full border font-medium ${
        compact
          ? "px-1.5 py-0.5 text-[10px] leading-tight sm:text-[11px]"
          : "px-2.5 py-1 text-xs font-semibold"
      } ${cls} ${className}`}
      title={full}
    >
      {label}
    </span>
  );
}

/** Status pill (Active, Deactivated, Pending) */
export function StatusPill({ status }) {
  const map = {
    Active: "bg-emerald-100 text-emerald-700",
    Deactivated: "bg-rose-100 text-rose-700",
    Pending: "bg-amber-100 text-amber-700",
  };
  const cls =
    map[status] || "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current/70" />
      {status}
    </span>
  );
}
